import React, { useMemo, useState } from "react";
import { X, Copy, Check, Facebook, Twitter, Instagram, Download, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { shareUrl, promoUrl } from "../api";

// TikTok isn't in lucide-react — inline the official glyph so it stays sharp at any size.
function TikTokIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

/**
 * Canonicalize the share URL so links always resolve to the production domain
 * (hiphopistheculture.com) rather than a preview / deploy-host subdomain.
 * Falls back to the current page URL if the env var is missing.
 */
function canonicalUrl(explicitUrl) {
  if (typeof window === "undefined") return explicitUrl || "";
  const base = (process.env.REACT_APP_CANONICAL_URL || "").replace(/\/$/, "");
  if (!base) return explicitUrl || window.location.href;
  const path = explicitUrl
    ? (() => { try { return new URL(explicitUrl, window.location.origin).pathname + new URL(explicitUrl, window.location.origin).search; } catch { return window.location.pathname + window.location.search; } })()
    : window.location.pathname + window.location.search;
  return `${base}${path}`;
}

/**
 * ShareSheet — bottom-anchored share modal.
 * Twitter / Facebook use standard intent URLs. Instagram + TikTok don't
 * expose a web share API, so those buttons copy the canonical link and
 * (on mobile) attempt to open the native app.
 */
export default function ShareSheet({ open, onClose, title, url, artist, ctype, id }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = useMemo(() => (ctype && id ? shareUrl(ctype, id) : canonicalUrl(url)), [url, open, ctype, id]);
  const isVideo = ctype === "videos";
  if (!open) return null;

  const verb = isVideo ? "Watch" : "Listen to";
  const shareText = artist ? `${verb} ${title} by ${artist} on Hip Hop Is The Culture` : `${verb} ${title}`;
  const encoded = encodeURIComponent(fullUrl);
  const encodedText = encodeURIComponent(shareText);

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      return true;
    } catch {
      return false;
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text: shareText, url: fullUrl }); onClose?.(); }
      catch { /* dismissed */ }
    } else {
      const ok = await doCopy();
      toast[ok ? "success" : "error"](ok ? "Link copied" : "Couldn't copy");
    }
  };

  const shareToApp = async ({ label, deepLink, webFallback }) => {
    // 1) Prefer OS share sheet on mobile.
    if (navigator.share) {
      try { await navigator.share({ title, text: shareText, url: fullUrl }); onClose?.(); return; }
      catch { /* user dismissed — fall through to copy */ }
    }
    // 2) Copy the link so the user can paste inside the target app.
    const ok = await doCopy();
    toast[ok ? "success" : "error"](
      ok ? `Link copied — paste it into ${label}` : `Couldn't copy`
    );
    // 3) Try to open the app (works on mobile; harmless on desktop).
    if (deepLink && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "")) {
      window.location.href = deepLink;
      return;
    }
    if (webFallback) window.open(webFallback, "_blank", "noopener");
  };

  const targets = [
    {
      key: "twitter",   label: "X (Twitter)",  Icon: Twitter,
      onClick: () => window.open(`https://twitter.com/intent/tweet?url=${encoded}&text=${encodedText}`, "_blank", "noopener"),
    },
    {
      key: "facebook",  label: "Facebook", Icon: Facebook,
      onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encoded}`, "_blank", "noopener"),
    },
    {
      key: "instagram", label: "Instagram", Icon: Instagram,
      onClick: () => shareToApp({
        label: "Instagram",
        deepLink: "instagram://library",
        webFallback: "https://www.instagram.com/",
      }),
    },
    {
      key: "tiktok",    label: "TikTok",    Icon: TikTokIcon,
      onClick: () => shareToApp({
        label: "TikTok",
        deepLink: "snssdk1233://",
        webFallback: "https://www.tiktok.com/upload",
      }),
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="share-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[var(--surface)] border border-neutral-800 sm:border-t rounded-t-2xl sm:rounded-none"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800">
          <div>
            <div className="font-mono-label text-[10px] text-[var(--accent)]">{isVideo ? "SHARE THE VIDEO" : ctype === "singles" ? "SHARE THE SINGLE" : "SHARE THE TAPE"}</div>
            <div className="font-headline text-lg uppercase truncate max-w-[260px]">{title}</div>
          </div>
          <button onClick={onClose} data-testid="share-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {typeof navigator !== "undefined" && navigator.share && (
            <button
              onClick={nativeShare}
              className="w-full flex items-center justify-center gap-2 py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-mono-label text-xs uppercase tracking-wider"
              data-testid="share-native"
            >
              Share via device…
            </button>
          )}

          <div className="grid grid-cols-4 gap-2">
            {targets.map(({ key, label, Icon, onClick }) => (
              <button
                key={key}
                onClick={onClick}
                className="flex flex-col items-center gap-2 py-3 border border-neutral-800 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                data-testid={`share-${key}`}
              >
                <Icon size={20} />
                <span className="font-mono-label text-[9px] uppercase">{label}</span>
              </button>
            ))}
          </div>

          {ctype && id && (
            <div data-testid="share-promo-row">
              <div className="font-mono-label text-[10px] text-neutral-400 mb-2">PROMO IMAGES · POST TO STORIES & FEED</div>
              <div className="grid grid-cols-2 gap-2">
                {[["story", "Story 9:16"], ["feed", "Feed 1:1"]].map(([fmt, label]) => (
                  <a key={fmt} href={promoUrl(ctype, id, fmt, true)} download
                    className="flex items-center justify-center gap-2 py-3 border border-neutral-800 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors font-mono-label text-[10px] uppercase"
                    data-testid={`share-promo-${fmt}`}>
                    <Download size={14} /> {label}
                  </a>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={async () => {
              const ok = await doCopy();
              toast[ok ? "success" : "error"](ok ? "Link copied" : "Couldn't copy");
            }}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-neutral-800 hover:border-[var(--accent)] transition-colors"
            data-testid="share-copy"
          >
            <div className="flex items-center gap-3 min-w-0">
              <LinkIcon size={16} />
              <span className="font-mono-label text-[11px] text-neutral-300 truncate">{fullUrl}</span>
            </div>
            <span className="font-mono-label text-[10px] uppercase text-[var(--accent)] flex items-center gap-1">
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
